import { useCallback, useMemo, useRef } from "react";
import { useGoogleText } from "./useGoogleText";

/** React owns attribute sources; Google owns their translations. Reapply the
 * latest source's result if Google's restore cache writes an older attribute. */
export function useGoogleAttributes<T extends Element>(
  sources: Record<string, string | undefined>,
) {
  const sourceKey = JSON.stringify(sources);
  const translate = useGoogleText(
    Object.values(sources).filter(
      (value): value is string => typeof value === "string",
    ),
  );
  const attributes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(JSON.parse(sourceKey) as Record<string, string>).map(
          ([name, source]) => [name, translate(source)],
        ),
      ),
    [sourceKey, translate],
  );
  const observer = useRef<MutationObserver | null>(null);
  const ref = useCallback(
    (element: T | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!element) return;
      const apply = () => {
        for (const [name, value] of Object.entries(attributes))
          if (element.getAttribute(name) !== value)
            element.setAttribute(name, value);
      };
      apply();
      if (Object.keys(attributes).length) {
        observer.current = new MutationObserver(apply);
        observer.current.observe(element, {
          attributes: true,
          attributeFilter: Object.keys(attributes),
        });
      }
    },
    [attributes],
  );
  return { ref, attributes };
}
